import { c as _c } from "react/compiler-runtime";
import { IcoArrowTopRight16, IcoAt16, IcoChevronDown16 } from '@onefootprint/icons';
import { CopyButton, Dropdown } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useChatInputMention } from '../chat-input';
import type { MentionKind } from '../chat-input/mention-extension';
import { getEntityHref, getEntityKindFromId, getIconForKind } from '../chat-input/mention-extension';
import { useMessageMentionName } from './message-mentions-context';
type IdBadgeProps = {
  id: string;
  /** `default` — for use inside Percy's reply markdown (host text is 14px).
   *  `compact` — for use inside tool-call rows (host text is 13px). */
  size?: 'default' | 'compact';
};

// Inline-text pill modeled on IdDropdown: clicking the ID copies it (with a
// "Copy to clipboard" / "Copied!" tooltip); clicking the trailing chevron opens
// a small menu with Go to / Mention actions. Inside a user message whose
// `mentions` snapshot carried this id, the pill shows the friendly name in
// place of the raw id (see MessageMentionsProvider).
const IdBadge = t0 => {
  const $ = _c(13);
  const {
    id,
    size: t1
  } = t0;
  const size = t1 === undefined ? "default" : t1;
  let t2;
  if ($[0] !== id) {
    t2 = getEntityKindFromId(id);
    $[0] = id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const kind = t2;
  const compact = size === "compact";
  const mentionName = useMessageMentionName(id);
  const t3 = mentionName ? "" : "font-mono";
  const t4 = compact ? "text-[11px] px-1 py-0" : "text-[13px] px-1 py-0";
  let t5;
  if ($[2] !== t3 || $[3] !== t4) {
    t5 = cx("text-accent bg-secondary border border-tertiary rounded align-baseline", t3, t4);
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const codeClass = t5;
  if (!kind) {
    const t6 = mentionName ?? id;
    let t7;
    if ($[5] !== codeClass || $[6] !== t6) {
      t7 = <code className={codeClass}>{t6}</code>;
      $[5] = codeClass;
      $[6] = t6;
      $[7] = t7;
    } else {
      t7 = $[7];
    }
    return t7;
  }
  let t6;
  if ($[8] !== compact || $[9] !== id || $[10] !== kind || $[11] !== mentionName) {
    t6 = <IdBadgeMenu id={id} kind={kind} compact={compact} name={mentionName} />;
    $[8] = compact;
    $[9] = id;
    $[10] = kind;
    $[11] = mentionName;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  return t6;
};
type IdBadgeMenuProps = {
  id: string;
  kind: MentionKind;
  compact: boolean;
  name: string | undefined;
};

// Onboarding is intentionally absent: it has no Go-to link, so it needs no label.
const KIND_TRANSLATION_KEY: Partial<Record<MentionKind, 'person' | 'business' | 'playbook' | 'investigation' | 'agentVersion' | 'agent'>> = {
  person: 'person',
  business: 'business',
  playbook: 'playbook',
  investigation: 'investigation',
  agent_version: 'agentVersion',
  agent: 'agent'
};
const IdBadgeMenu = t0 => {
  const $ = _c(41);
  const {
    id,
    kind,
    compact,
    name
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "idBadge"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  const mention = useChatInputMention();
  const kindKey = KIND_TRANSLATION_KEY[kind];
  let t2;
  if ($[1] !== id || $[2] !== kind) {
    t2 = getEntityHref(id, kind);
    $[1] = id;
    $[2] = kind;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const href = t2;
  const hasMenu = !!href || !!mention;
  const displayText = name ?? id;
  let t3;
  if ($[4] !== kind) {
    t3 = getIconForKind(kind);
    $[4] = kind;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const Icon = t3;
  const t4 = name ? id : undefined;
  const t5 = name ? "" : "font-mono";
  const t6 = compact ? "text-[11px]" : "text-[13px]";
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = cx("inline-flex items-stretch overflow-hidden rounded border border-tertiary bg-secondary text-accent align-baseline", t5, t6);
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== Icon) {
    t8 = <span aria-hidden="true" className="shrink-0 inline-flex items-center pl-1 [&_svg]:w-3 [&_svg]:h-3"><Icon color="accent" /></span>;
    $[9] = Icon;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== id || $[12] !== t) {
    t9 = t(_temp, {
      id
    });
    $[11] = id;
    $[12] = t;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== name || $[15] !== t) {
    t10 = name ? t(_temp2) : undefined;
    $[14] = name;
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = {
      position: "top",
      text: t10
    };
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== displayText) {
    t12 = <span>{displayText}</span>;
    $[19] = displayText;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== id || $[22] !== t11 || $[23] !== t12 || $[24] !== t9) {
    t13 = <CopyButton contentToCopy={id} ariaLabel={t9} tooltip={t11} className="pl-0.5 pr-1 transition-colors">{t12}</CopyButton>;
    $[21] = id;
    $[22] = t11;
    $[23] = t12;
    $[24] = t9;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== displayText || $[27] !== hasMenu || $[28] !== href || $[29] !== id || $[30] !== kind || $[31] !== kindKey || $[32] !== mention || $[33] !== t) {
    t14 = hasMenu && <Dropdown.Root><Dropdown.Trigger asChild={true}><button type="button" aria-label={t(_temp3, {
          id
        })} className={cx("group inline-flex items-center justify-center px-1 cursor-pointer transition-colors", "relative before:content-[\"\"] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:w-px before:h-2.5 before:bg-(--border-color-tertiary)")}><IcoChevronDown16 className="w-3! h-3! transition-transform duration-100 ease-in-out group-data-[state=open]:rotate-180" /></button></Dropdown.Trigger><Dropdown.Portal><Dropdown.Content align="center" sideOffset={4} className="min-w-[200px] p-1">{href && kindKey && <Dropdown.Item size="compact" iconLeft={IcoArrowTopRight16} asChild={true}><Link href={href}>{t($_2 => $_2.goTo[kindKey])}</Link></Dropdown.Item>}{mention && <Dropdown.Item size="compact" iconLeft={IcoAt16} onSelect={() => mention.mentionEntity({
            id,
            label: displayText,
            kind
          })}>{t(_temp4)}</Dropdown.Item>}</Dropdown.Content></Dropdown.Portal></Dropdown.Root>;
    $[26] = displayText;
    $[27] = hasMenu;
    $[28] = href;
    $[29] = id;
    $[30] = kind;
    $[31] = kindKey;
    $[32] = mention;
    $[33] = t;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  let t15;
  if ($[35] !== t13 || $[36] !== t14 || $[37] !== t4 || $[38] !== t7 || $[39] !== t8) {
    t15 = <span title={t4} className={t7}>{t8}{t13}{t14}</span>;
    $[35] = t13;
    $[36] = t14;
    $[37] = t4;
    $[38] = t7;
    $[39] = t8;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
};
export default IdBadge;
function _temp($) {
  return $.copyAriaLabel;
}
function _temp2($_0) {
  return $_0.copyIdTooltip;
}
function _temp3($_1) {
  return $_1.menuAriaLabel;
}
function _temp4($_3) {
  return $_3.mention;
}